import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Card,
  Button,
  Typography,
  Modal,
  Space,
  Tooltip,
  message,
  Drawer,
  Timeline,
  Descriptions,
  Tag,
  Popover,
} from 'antd';
import { EyeOutlined, EyeInvisibleOutlined, UndoOutlined } from '@ant-design/icons';
import { KeypointCollectionLabel } from 'models/keypoints';
import { CoverWrapper, FrameImage } from '../../SegmentationLabels/styled';
import ImageComparator from 'components/Shared/components/imageComparator';
import {
  useCreateReviewMutation,
  useBulkDeleteReviewsMutation,
} from '../../../redux/slices/reviews';
import { Decision, ReviewIn } from '../../../models/reviews';
import { KeypointFrame } from '../../../models/keypoints';
import moment from 'moment';
import { VscHistory } from 'react-icons/vsc';
import { BiMessageSquareEdit } from 'react-icons/bi';
import { BsHandThumbsDown, BsHandThumbsUp } from 'react-icons/bs';
import KeypointOverlay from './KeypointOverlay';
import { getColor } from 'theme/features';
import { COLORS } from 'theme';
import { config } from 'services/config';

interface KeypointItemProps {
  keypoint: KeypointCollectionLabel;
  frame: KeypointFrame;
  frameFile: string;
  review: ReviewIn | undefined;
  acceptMe: boolean;
  allKeypoints: KeypointCollectionLabel[];
  onKeypointToggle: (keypointId: string, visible: boolean) => void;
  visibleKeypoints: Set<string>;
  onFrameAccept?: () => void;
  onFrameReject?: () => void;
  frameReviewed: boolean;
  onReviewChange: () => void;
  getReviewFromKeypoint: (keypoint_uuid: string) => ReviewIn | undefined;
  // Local decision helpers
  onLocalDecision?: (id: string, decision: Decision) => void;
  onLocalDecisionRemove?: (id: string) => void;
  getLocalDecision?: (id: string) => Decision | undefined;
  removedReviews?: Record<string, boolean>;
  onMarkRemoved?: (ids: string[]) => void;
  isRejected: boolean;
}

const RejectModal = ({ isModalOpen, handleCancel, handleOk }: any) => {
  return (
    <Modal title="Reject Keypoint" open={isModalOpen} onCancel={handleCancel} footer={null}>
      <Typography.Text>Are you sure you want to reject this keypoint?</Typography.Text>
      <Space>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button type="primary" onClick={() => handleOk('Rejected')}>
          Reject
        </Button>
      </Space>
    </Modal>
  );
};

const KeypointItem: React.FC<KeypointItemProps> = ({
  keypoint,
  frame,
  frameFile,
  review,
  allKeypoints,
  onKeypointToggle,
  visibleKeypoints,
  onFrameAccept,
  onFrameReject,
  frameReviewed,
  onReviewChange,
  onLocalDecision,
  onLocalDecisionRemove,
  getLocalDecision,
  removedReviews,
  onMarkRemoved,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createReview] = useCreateReviewMutation();
  const [bulkDeleteReviews] = useBulkDeleteReviewsMutation();
  const [showReverseConfirm, setShowReverseConfirm] = useState(false);

  const coverWrapperRef = useRef<HTMLDivElement>(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
  // const [scaleFactor, setScaleFactor] = useState(1); // reserved for future zoom features
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const handleDicomClick = () => {
    const token = localStorage.getItem('AccessToken');
    if (frame.dicomIcid) {
      window.open(
        `${config.ATLAS_URL}/?dicom=${encodeURIComponent(frame.dicomIcid)}${token ? `&t=${encodeURIComponent(token)}` : ''}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  const calculateImagePosition = useCallback((img: HTMLImageElement, container: HTMLElement) => {
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const imgWidth = img.naturalWidth;
    const imgHeight = img.naturalHeight;

    const scale = Math.min(containerWidth / imgWidth, containerHeight / imgHeight);
    const displayWidth = imgWidth * scale;
    const displayHeight = imgHeight * scale;
    const offsetX = (containerWidth - displayWidth) / 2;
    const offsetY = (containerHeight - displayHeight) / 2;

    return {
      scale,
      offsetX,
      offsetY,
      displayWidth,
      displayHeight,
      imgWidth,
      imgHeight,
    };
  }, []);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.target as HTMLImageElement;
    const container = coverWrapperRef.current;

    if (container) {
      const { offsetX, offsetY, displayWidth, displayHeight, imgWidth, imgHeight } =
        calculateImagePosition(img, container);

      setImageDimensions({ width: imgWidth, height: imgHeight });
      setContainerDimensions({ width: displayWidth, height: displayHeight });
      // scale intentionally unused
      setOffset({ x: offsetX, y: offsetY });
    }
  };

  // removed unused getKeypointPosition

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      const img = coverWrapperRef.current?.querySelector('img');
      if (img && img.complete) {
        handleImageLoad({ target: img } as unknown as React.SyntheticEvent<HTMLImageElement>);
      }
    });

    if (coverWrapperRef.current) {
      observer.observe(coverWrapperRef.current);
    }

    return () => {
      if (coverWrapperRef.current) {
        observer.unobserve(coverWrapperRef.current);
      }
    };
  }, []);

  const decision =
    review?.decision ?? (getLocalDecision ? getLocalDecision(keypoint.icid) : undefined);

  const handleReject = async (comment: string) => {
    setIsSubmitting(true);
    try {
      await createReview({
        object_uuid: keypoint.uuid,
        object_icid: keypoint.icid,
        object_type: 'KEYPOINT_COLLECTION_LABEL',
        object_view: 'default',
        object_view_id: 0,
        decision: Decision.REJECTED,
        comment: comment,
        annotation_link: '',
      }).unwrap();
      message.success('Keypoint rejected successfully');
      if (onLocalDecision) onLocalDecision(keypoint.icid, Decision.REJECTED);
      onReviewChange();
    } catch (error) {
      message.error('Failed to reject keypoint');
    } finally {
      setIsSubmitting(false);
      setIsRejectModalOpen(false);
    }
  };

  const handleAccept = async () => {
    try {
      await createReview({
        object_uuid: keypoint.uuid,
        object_icid: keypoint.icid,
        object_type: 'KEYPOINT_COLLECTION_LABEL',
        object_view: 'default',
        object_view_id: 0,
        decision: Decision.ACCEPTED,
        comment: '',
        annotation_link: '',
      }).unwrap();
      message.success('Keypoint accepted successfully');
      if (onLocalDecision) onLocalDecision(keypoint.icid, Decision.ACCEPTED);
      onReviewChange();
    } catch (error) {
      message.error('Failed to accept keypoint');
    }
  };

  const history: any[] = [];

  const historyItems = history.map((item: any) => ({
    label: moment(item.timestamp).format('LLL'),
    children: (
      <>
        <Typography.Text>{item.technician_email}</Typography.Text>
        <p>{item.comment}</p>
      </>
    ),
    color: item.decision === Decision.ACCEPTED ? 'green' : 'red',
  }));

  const items: Parameters<typeof Descriptions>[0]['items'] = [
    {
      key: '1',
      label: 'Dicom',
      span: { xs: 24, sm: 12, md: 8, lg: 8, xl: 6, xxl: 6 },
      children: (
        <Popover content="Click to view this DICOM on Atlas" trigger="hover">
          <Typography.Link onClick={handleDicomClick} style={{ cursor: 'pointer' }}>
            {frame.dicomIcid}
          </Typography.Link>
        </Popover>
      ),
    },
    {
      key: '2',
      label: 'Datasource',
      span: { xs: 24, sm: 12, md: 8, lg: 8, xl: 6, xxl: 6 },
      children: (
        <Tag bordered={false} color={getColor(frame.datasourceName || 'default')}>
          {frame.datasourceName || 'N/A'}
        </Tag>
      ),
    },

    {
      key: '3',
      label: 'Frame Index',
      span: { xs: 24, sm: 12, md: 8, lg: 8, xl: 6, xxl: 6 },
      children: <Typography.Text>{keypoint.frameIndex}</Typography.Text>,
    },
  ];

  const keypointCover = () => {
    return (
      <CoverWrapper ref={coverWrapperRef} style={{ position: 'relative' }}>
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {allKeypoints.map(kp => {
              const isVisible = visibleKeypoints.has(kp.icid);
              // Single effective state per keypoint (use ICID for local decisions)
              const localD = getLocalDecision ? getLocalDecision(kp.icid) : undefined;
              let effective: Decision | undefined = localD;
              if (effective === undefined) {
                const wasRemoved = removedReviews ? !!removedReviews[kp.icid] : false;
                if (!wasRemoved) {
                  if (kp.isRejected) effective = Decision.REJECTED;
                  else if (kp.isApproved) effective = Decision.ACCEPTED;
                }
              }
              const kpAccepted = effective === Decision.ACCEPTED;
              const kpRejected = effective === Decision.REJECTED;
              return (
                <div
                  key={kp.icid}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'rgba(255, 255, 255, 0.7)',
                    padding: '4px 8px',
                    borderRadius: '16px',
                  }}
                >
                  {(() => {
                    const hasLabels =
                      Array.isArray(kp.keypointLabels) && kp.keypointLabels.length > 0;
                    const tooltipContent: React.ReactNode = hasLabels ? (
                      <div>
                        <div>
                          {isVisible
                            ? `Hide ${kp.keypointCollectionClassValue}`
                            : `Show ${kp.keypointCollectionClassValue}`}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            marginTop: 6,
                            color: 'rgba(255,255,255,0.9)',
                            opacity: 0.95,
                          }}
                        >
                          {`ICID: ${kp.icid}`}
                        </div>
                      </div>
                    ) : (
                      'Keypoints not available'
                    );

                    return (
                      <Tooltip title={tooltipContent}>
                        <Button
                          shape="circle"
                          size="small"
                          disabled={!hasLabels}
                          style={{
                            cursor: !hasLabels ? 'not-allowed' : undefined,
                            opacity: !hasLabels ? 0.45 : 1,
                          }}
                          icon={
                            isVisible ? (
                              <EyeOutlined
                                style={{
                                  fontSize: '14px',
                                  color: !hasLabels ? COLORS.lightDark : undefined,
                                }}
                              />
                            ) : (
                              <EyeInvisibleOutlined
                                style={{
                                  fontSize: '14px',
                                  color: !hasLabels ? COLORS.lightDark : undefined,
                                }}
                              />
                            )
                          }
                          onClick={e => {
                            e.stopPropagation();
                            if (!hasLabels) return;
                            onKeypointToggle(kp.icid, !isVisible);
                          }}
                        />
                      </Tooltip>
                    );
                  })()}
                  {kpAccepted && (
                    <Tooltip title="Accepted">
                      <BsHandThumbsUp color="green" style={{ fontSize: '14px', marginLeft: 4 }} />
                    </Tooltip>
                  )}
                  {kpRejected && (
                    <Tooltip title="Rejected">
                      <BsHandThumbsDown color="red" style={{ fontSize: '14px', marginLeft: 4 }} />
                    </Tooltip>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {frameFile ? (
          <FrameImage
            src={frameFile}
            alt={`Keypoint ${keypoint.icid}`}
            onClick={() => setIsModalVisible(true)}
            style={{ cursor: 'pointer' }}
            onLoad={handleImageLoad}
            onError={(e: any) => {
              e.target.onerror = null;
              e.target.src = '';
            }}
          />
        ) : (
          <Typography.Text type="secondary">No Image Available</Typography.Text>
        )}
        <KeypointOverlay
          frame={{
            datasourceName: frame.datasourceName,
            dicomIcid: frame.dicomIcid,
            fileUrl: frameFile,
            icid: frame.icid,
            index: frame.index,
            keypointCollectionLabels: allKeypoints.filter(kp => visibleKeypoints.has(kp.icid)),
            uuid: frame.uuid,
          }}
          keypoints={allKeypoints.filter(kp => visibleKeypoints.has(kp.icid))}
          displayDims={{
            left: offset.x,
            top: offset.y,
            width: containerDimensions.width,
            height: containerDimensions.height,
            imgW: imageDimensions.width,
            imgH: imageDimensions.height,
          }}
        />
      </CoverWrapper>
    );
  };

  return (
    <>
      <Modal
        title="Reject Keypoint"
        open={isRejectModalOpen}
        onCancel={() => setIsRejectModalOpen(false)}
        footer={null}
      >
        <RejectModal
          isModalOpen={isRejectModalOpen}
          handleCancel={() => setIsRejectModalOpen(false)}
          handleOk={handleReject}
        />
      </Modal>

      <Drawer
        title={keypoint.icid}
        onClose={() => setIsDrawerVisible(false)}
        open={isDrawerVisible}
      >
        <Timeline mode={'left'} items={historyItems} />
      </Drawer>
      <Modal
        title="Are you sure you want to reverse this review?"
        open={showReverseConfirm}
        onOk={async () => {
          setShowReverseConfirm(false);
          setIsSubmitting(true);
          try {
            const objectUuids = allKeypoints.map(kp => kp.uuid);
            const res = await bulkDeleteReviews({
              object_uuids: objectUuids,
              object_type: 'KEYPOINT_COLLECTION_LABEL',
            }).unwrap();
            const deleted = res?.deleted ?? 0;

            // Clear local decisions for all keypoints in this frame
            allKeypoints.forEach(kp => {
              if (onLocalDecisionRemove) onLocalDecisionRemove(kp.icid);
              else if (onLocalDecision) onLocalDecision(kp.icid, Decision.ACCEPTED);
            });

            // mark removed locally by parent if provided (Keypoints page)
            if (onMarkRemoved) onMarkRemoved(allKeypoints.map(k => k.icid));
            message.success(`Reversed ${deleted} review(s)`);
          } catch (err) {
            console.error('Failed to reverse keypoint reviews', err);
            message.error('Failed to reverse reviews');
          } finally {
            setIsSubmitting(false);
          }
        }}
        onCancel={() => setShowReverseConfirm(false)}
        okText="Reverse"
        cancelText="Cancel"
        centered
        maskClosable={false}
        confirmLoading={isSubmitting}
      >
        <p>This will remove all accept/reject reviews for the keypoints on this frame.</p>
      </Modal>

      <Card
        bordered={false}
        cover={keypointCover()}
        actions={[
          <Tooltip title="Reject" key="reject">
            <Button
              type={'text'}
              icon={<BsHandThumbsDown />}
              disabled={onFrameReject === undefined && decision === Decision.REJECTED}
              onClick={() => {
                if (onFrameReject) {
                  onFrameReject();
                } else {
                  setIsRejectModalOpen(true);
                }
              }}
            />
          </Tooltip>,
          <Tooltip title="Accept" key="accept">
            <Button
              type={'text'}
              icon={<BsHandThumbsUp />}
              disabled={
                isSubmitting || frameReviewed || (!onFrameAccept && decision === Decision.ACCEPTED)
              }
              onClick={() => {
                if (onFrameAccept) {
                  onFrameAccept();
                } else {
                  handleAccept();
                }
              }}
            />
          </Tooltip>,
          <Tooltip title="Undo" key="reverse">
            <Button
              type={'text'}
              icon={<UndoOutlined />}
              onClick={() => {
                // open confirmation modal for reverse
                setShowReverseConfirm(true);
              }}
            />
          </Tooltip>,
          // History button removed per request
          <Tooltip title="Edit" key="edit">
            <Button type={'text'} icon={<BiMessageSquareEdit />} disabled={true} />
          </Tooltip>,
        ]}
      >
        <Descriptions
          size="small"
          items={items}
          bordered
          column={{ xs: 1, sm: 2, md: 3, lg: 3, xl: 4, xxl: 4 }}
        />
      </Card>

      <Modal
        title="Keypoint Details"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width="80%"
        destroyOnClose
      >
        <ImageComparator
          leftFrame={{
            datasourceName: frame.datasourceName,
            dicomIcid: frame.dicomIcid,
            fileUrl: frameFile,
            icid: frame.icid,
            index: frame.index,
            keypointCollectionLabels: allKeypoints,
            uuid: frame.uuid,
          }}
          rightFrame={{
            datasourceName: frame.datasourceName,
            dicomIcid: frame.dicomIcid,
            fileUrl: frameFile,
            icid: frame.icid,
            index: frame.index,
            keypointCollectionLabels: allKeypoints,
            uuid: frame.uuid,
          }}
          leftSegmentations={[]}
          rightSegmentations={[]}
        />
      </Modal>
    </>
  );
};

export default KeypointItem;
